'use client';

import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ShieldCheck,
  User,
  X,
  WarningCircle,
  Phone,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
} from '@/components/ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useAuthStore } from '@/stores/auth-store';
import { authApi } from '@/lib/api/auth';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Image from 'next/image';
import { ymAuthSuccess, ymRegistration, ymAuthOpen, tmrRegistration, tmrAuthSuccess, tmrAuthOpen } from '@/lib/analytics';
import { captureEvent } from '@/lib/source-tracker';
import { startVkAuth } from '@/lib/vk-auth';

/* ─── CDN illustrations ─── */
const AUTH_IMAGES = {
  login: 'https://cdn.aziatochka.ru/aziatochka/login/login.webp',
  code: 'https://cdn.aziatochka.ru/aziatochka/login/code.webp',
  success: 'https://cdn.aziatochka.ru/aziatochka/login/succses.webp',
  error: 'https://cdn.aziatochka.ru/aziatochka/login/notvalid.webp',
} as const;

/* ─── helpers ─── */

function formatPhone(value: string): string {
  let d = value.replace(/\D/g, '');
  if (!d.length) return '+7 (';
  if (d.length >= 11 && (d.startsWith('7') || d.startsWith('8'))) {
    d = d.slice(1);
  } else if (d.length <= 10 && d.startsWith('7')) {
    d = d.slice(1);
  }
  d = d.slice(0, 10);
  if (!d.length) return '+7 (';
  if (d.length <= 3) return `+7 (${d}`;
  if (d.length <= 6) return `+7 (${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 8) return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

function normalizePhone(phone: string): string {
  let d = phone.replace(/\D/g, '');
  if (d.length === 11 && (d.startsWith('7') || d.startsWith('8'))) {
    return '7' + d.slice(1);
  }
  if (d.length === 10) return '7' + d;
  return d;
}

// Бэк принимает только российские мобильные (normalizeRussianMobile в callAbuseGuard).
// Без этой проверки кнопка «Получить код» была активна для казахстанских (77x)
// и городских (74x) номеров, а отказ приходил уже с сервера.
function isRussianMobile(normalized: string): boolean {
  return /^79\d{9}$/.test(normalized);
}

/* ─── события авторизации ─── */

type AuthMethod = 'flash' | 'callme' | 'vk';

type AuthEventProps = {
  method: AuthMethod;
  http_status?: number;
  error_code?: string;
  retry_after?: number;
};

// Трекер не должен ронять вход: если он не инициализирован, исключение гасим.
function trackAuth(
  name: 'auth_started' | 'auth_failed' | 'auth_success',
  props: AuthEventProps,
): void {
  try {
    captureEvent(name, props);
  } catch {
    // аналитика никогда не срывает основной поток
  }
}

/* ─── разбор ответа бэка об ошибке ─── */

type ApiErrorInfo = {
  message: string;
  status?: number;
  errorCode?: string;
  retryAfter?: number;
};

// Бэк отдаёт готовый текст (error), код причины (error_code) и паузу (retryAfter).
// Раньше всё это выбрасывалось, и на любую причину показывалось «Проверьте номер».
function readApiError(err: unknown, fallback: string): ApiErrorInfo {
  const response = (err as {
    response?: {
      status?: number;
      data?: { error?: string; message?: string; error_code?: string; retryAfter?: number };
    };
  })?.response;
  const data = response?.data;

  return {
    message: data?.error || data?.message || fallback,
    status: response?.status,
    errorCode: data?.error_code,
    retryAfter: typeof data?.retryAfter === 'number' ? data.retryAfter : undefined,
  };
}

/* ─── circular illustration (mobile only) ─── */

function AuthIllustration({ src, alt }: { src: string; alt: string }) {
  // SVG clipPath: rectangle on top, smooth arc (circle) on sides & bottom.
  // viewBox is 144×192 (w-36=144px, h-48=192px).
  // The circle is 144×144 sitting at the bottom (y 48–192).
  // The clip shape: rect from top-left to where the circle starts,
  // then an arc tracing the bottom half of the circle.
  const clipId = `auth-circle-clip-${alt.replace(/\s/g, '')}`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex justify-center mb-5 md:hidden"
    >
      {/* w-48=192px circle, h-60=240px container for overflow */}
      <div className="relative w-48 h-60">
        {/* background circle */}
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-pill bg-[var(--surface-secondary)]" />

        {/* SVG clip definition (hidden) */}
        <svg className="absolute w-0 h-0" aria-hidden="true">
          <defs>
            <clipPath id={clipId} clipPathUnits="objectBoundingBox">
              {/* 
                Container: 192×240. Circle: 192 diameter, bottom-aligned.
                Circle center in bbox: x=96/192=0.5, y=(240-96)/240=0.6
                Circle radius: rx=96/192=0.5, ry=96/240=0.4
                Arc starts at (1, 0.6) and ends at (0, 0.6) — the horizontal center line of the circle.
                Top is fully open (rect from 0,0 to 1,0.6), bottom is clipped by the arc.
              */}
              <path d={`
                M 0 0
                L 1 0
                L 1 0.6
                A 0.5 0.4 0 0 1 0 0.6
                Z
              `} />
            </clipPath>
          </defs>
        </svg>

        {/* clipped image container */}
        <div
          className="absolute inset-0"
          style={{ clipPath: `url(#${clipId})` }}
        >
          <Image
            src={src}
            alt={alt}
            width={240}
            height={300}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-none h-auto object-contain pointer-events-none"
            unoptimized
          />
        </div>
      </div>
    </motion.div>
  );
}

/* ─── step indicator ─── */

function StepDots({ current, total = 3 }: { current: number; total?: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'h-1 rounded-pill transition-all duration-300',
            i === current
              ? 'w-6 bg-[var(--primary)]'
              : 'w-1.5 bg-[var(--border-default)]',
          )}
        />
      ))}
    </div>
  );
}

/* ─── transition: spinner overlay ─── */

function AuthSpinner({ label }: { label: string }) {
  return (
    <motion.div
      key="spinner"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col items-center justify-center gap-5 py-12"
    >
      <div className="auth-spinner" />
      <p className="text-sm font-medium text-[var(--text-secondary)] animate-pulse">
        {label}
      </p>
    </motion.div>
  );
}

/* ─── transition: success screen ─── */

function AuthSuccess({ label }: { label: string }) {
  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col items-center justify-center gap-4 py-8"
    >
      <AuthIllustration src={AUTH_IMAGES.success} alt="Успешный вход" />
      <div className="auth-success-circle md:flex hidden">
        <svg className="auth-success-check" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p className="text-base font-semibold text-[var(--text-primary)]">{label}</p>
    </motion.div>
  );
}

/* ─── error screen ─── */

function AuthError({
  message,
  onRetry,
  onBack,
  retryAfter = 0,
}: {
  message: string;
  onRetry?: () => void;
  onBack: () => void;
  retryAfter?: number;
}) {
  return (
    <motion.div
      key="error"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col items-center justify-center gap-4 py-8"
    >
      <AuthIllustration src={AUTH_IMAGES.error} alt="Ошибка входа" />
      {/* desktop fallback icon */}
      {/* red-500 заменён на роль --error: в светлой теме это тот же #EF4444,
          в тёмной — #F87171, который не «горит» на глубоком фиолетовом. */}
      <div className="hidden md:flex w-16 h-16 rounded-pill bg-[var(--error)]/10 items-center justify-center">
        <WarningCircle className="w-8 h-8 text-[var(--error)]" weight="duotone" />
      </div>
      <div className="text-center space-y-1">
        <p className="text-base font-semibold text-[var(--text-primary)]">Не удалось войти</p>
        <p className="text-sm text-[var(--text-secondary)]">{message}</p>
        {retryAfter > 0 && (
          <p className="text-sm text-[var(--text-tertiary)]">
            Повторить можно через{' '}
            <span className="font-medium text-[var(--text-secondary)]">{retryAfter} сек</span>.
            {' '}Пока можно войти через VK ID — нажмите «Вернуться назад».
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 w-full mt-2">
        {onRetry && (
          <Button onClick={onRetry} className="w-full" size="xl">
            Попробовать снова
          </Button>
        )}
        <Button variant="outline" onClick={onBack} className="w-full" size="xl">
          Вернуться назад
        </Button>
      </div>
    </motion.div>
  );
}

/* ─── code input ─── */

function CodeInput({
  value,
  onChange,
  disabled,
  length = 4,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  length?: number;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!disabled) inputRefs.current[0]?.focus();
  }, [disabled]);

  const handleChange = (index: number, char: string) => {
    if (!/^\d?$/.test(char)) return;
    const arr = value.split('');
    arr[index] = char;
    onChange(arr.join('').slice(0, length));
    if (char && index < length - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            'w-14 h-14 max-md:w-16 max-md:h-16 text-center text-2xl font-bold',
            'rounded-card border-2 border-[var(--border-default)]',
            'bg-[var(--surface-secondary)]',
            'text-[var(--text-primary)]',
            'focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            value[i] && 'border-[var(--primary)] bg-[var(--primary-subtle)]',
          )}
        />
      ))}
    </div>
  );
}

/* ─── VK button (redirect) ─── */

/**
 * Показывать ли вход через VK. Выключен 17.08.2026 по просьбе владельца,
 * формулировка «пока что» — поэтому флаг, а не удаление кода.
 * Вернуть: поставить true.
 */
const SOCIAL_LOGIN_ENABLED = false;

function VkButton() {
  const handleClick = async () => {
    // Событие уходит через sendBeacon — переживает уход на id.vk.com
    trackAuth('auth_started', { method: 'vk' });
    await startVkAuth();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full h-12 rounded-card bg-[#0077FF] hover:bg-[#0066DD] active:bg-[#005BBF] text-white font-medium t-body flex items-center justify-center transition-colors duration-150 cursor-pointer"
    >
      Войти через VK ID
    </button>
  );
}

/* ─── phone view ─── */

function PhoneView({
  onSubmitPhone,
  isLoading,
}: {
  onSubmitPhone: (phone: string) => void;
  isLoading: boolean;
}) {
  const [phone, setPhone] = useState('+7 (');
  const [error, setError] = useState<string | undefined>();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(undefined);
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = normalizePhone(phone);
    if (isRussianMobile(n)) onSubmitPhone(n);
    else setError('Введите российский мобильный номер: +7 9XX XXX-XX-XX');
  };

  const normalized = normalizePhone(phone);
  const isValid = isRussianMobile(normalized);
  // Номер набран целиком, но это не РФ-мобильный: объясняем сразу, иначе кнопка
  // просто остаётся серой и человек не понимает, чего от него хотят.
  const invalidHint =
    !isValid && normalized.length === 11
      ? 'Нужен российский мобильный: +7 9XX XXX-XX-XX'
      : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col"
    >
      {/* Mobile illustration */}
      <AuthIllustration src={AUTH_IMAGES.login} alt="Вход в аккаунт" />

      {/* Desktop header — hidden on mobile (navbar handles it) */}
      <DialogHeader className="mb-6 hidden md:block">
        <DialogTitle className="text-xl font-heading flex items-center gap-2">
          <div className="w-8 h-8 rounded-pill bg-[var(--primary-subtle)] items-center justify-center flex-shrink-0 flex">
            <User className="w-4 h-4 text-[var(--primary)]" weight="duotone" />
          </div>
          Вход в аккаунт
        </DialogTitle>
        <DialogDescription className="mt-1.5">
          Введите номер телефона для получения кода
        </DialogDescription>
      </DialogHeader>

      {/* Mobile subtitle */}
      <p className="t-body text-[var(--text-secondary)] text-center mb-5 md:hidden">
        Введите номер телефона для получения кода
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="tel"
          label="Номер телефона"
          value={phone}
          onChange={handlePhoneChange}
          error={error || invalidHint}
          disabled={isLoading}
          size="lg"
        />

        <Button
          type="submit"
          disabled={!isValid || isLoading}
          isLoading={isLoading}
          className="w-full"
          size="xl"
        >
          Получить код
        </Button>

        {/*
          ВХОД ЧЕРЕЗ VK ВЫКЛЮЧЕН — решение владельца от 17.08.2026, «пока что».
          Вместе с кнопкой убран и разделитель «или»: черта без содержимого под
          ней читается как недогрузившийся блок.

          Путь входа остался один — по номеру телефона. Телеграм убрали в тот
          же день. Проверено перед выключением: из 452 аккаунтов с привязанным
          VK ни один не остаётся без входа, у всех заполнен телефон.

          Компонент VkButton и startVkAuth НЕ УДАЛЕНЫ намеренно: вернуть кнопку
          — снова отрендерить её здесь, ничего восстанавливать не придётся.
        */}
        {SOCIAL_LOGIN_ENABLED && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[var(--border-default)]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[var(--surface-primary)] px-2 text-[var(--text-tertiary)]">
                  или
                </span>
              </div>
            </div>

            <VkButton />
          </>
        )}

        <p className="text-center text-xs text-[var(--text-tertiary)] pt-1">
          Продолжая, вы соглашаетесь с{' '}
          <a href="/privacy" className="text-[var(--primary)] hover:underline">
            политикой конфиденциальности
          </a>
        </p>
      </form>
    </motion.div>
  );
}

/* ─── verify view ─── */

function VerifyView({
  phone,
  onVerify,
  onBack,
  onResend,
  isLoading,
  error,
  errorNonce,
}: {
  phone: string;
  requestId: string;
  onVerify: (code: string) => void;
  onBack: () => void;
  onResend: () => void;
  isLoading: boolean;
  error?: string;
  errorNonce: number;
}) {
  const [code, setCode] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  // Код, который уже ушёл на проверку. Без этого повторный рендер родителя
  // (onVerify пересоздаётся каждый раз) отправлял бы тот же код заново.
  const submittedRef = useRef('');

  useEffect(() => {
    const id = setInterval(() => setResendTimer((p) => (p > 0 ? p - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  // Сигнал «сбросить поле» — неверный код или новый звонок по коду 6666.
  // Экран ввода при этом остаётся на месте, поэтому нужен счётчик: одинаковый
  // текст ошибки сам по себе поле не сбросил бы.
  useEffect(() => {
    if (errorNonce > 0) setCode('');
  }, [errorNonce]);

  useEffect(() => {
    if (code.length < 4) {
      submittedRef.current = '';
      return;
    }
    if (!isLoading && submittedRef.current !== code) {
      submittedRef.current = code;
      onVerify(code);
    }
  }, [code, isLoading, onVerify]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col"
    >
      {/* Mobile illustration */}
      <AuthIllustration src={AUTH_IMAGES.code} alt="Введите код" />

      {/* Desktop back + header — hidden on mobile */}
      <button
        type="button"
        onClick={onBack}
        disabled={isLoading}
        className="hidden md:flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors mb-5 disabled:opacity-50 self-start"
      >
        <ArrowLeft className="w-4 h-4" weight="bold" />
        Изменить номер
      </button>

      <DialogHeader className="mb-6 hidden md:block">
        <DialogTitle className="text-xl font-heading flex items-center gap-2">
          <div className="w-8 h-8 rounded-pill bg-[var(--primary-subtle)] items-center justify-center flex-shrink-0 flex">
            <ShieldCheck className="w-4 h-4 text-[var(--primary)]" weight="duotone" />
          </div>
          Подтверждение
        </DialogTitle>
        <DialogDescription className="mt-1.5">
          Звонок поступит на{' '}
          <span className="font-medium text-[var(--text-primary)]">{formatPhone(phone)}</span>
        </DialogDescription>
      </DialogHeader>

      {/* Mobile subtitle */}
      <p className="t-body text-[var(--text-secondary)] text-center mb-5 md:hidden">
        Звонок поступит на{' '}
        <span className="font-medium text-[var(--text-primary)]">{formatPhone(phone)}</span>
      </p>

      <div className="space-y-5">
        {/* Flash call notification banner */}
        <div className="flex items-start gap-3 rounded-card bg-[var(--primary-subtle)] p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-[var(--primary)]/15">
            <Phone className="h-5 w-5 text-[var(--primary)]" weight="duotone" />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-[var(--text-primary)]">
              Сейчас поступит звонок
            </p>
            <p className="t-caption text-[var(--text-secondary)]">
              Введите последние <span className="font-semibold text-[var(--primary)]">4 цифры</span> входящего номера
            </p>
          </div>
        </div>

        <CodeInput value={code} onChange={setCode} disabled={isLoading} />

        {error && !isLoading && (
          <p className="text-center text-sm text-[var(--error)]">{error}</p>
        )}

        {isLoading ? (
          <p className="text-center text-sm text-[var(--text-secondary)]">Проверяем код...</p>
        ) : resendTimer > 0 ? (
          <p className="text-center text-sm text-[var(--text-tertiary)]">
            Повторная отправка через{' '}
            <span className="font-medium text-[var(--text-secondary)]">{resendTimer} сек</span>
          </p>
        ) : (
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => { setCode(''); setResendTimer(60); onResend(); }}
            >
              Отправить код повторно
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── call me view (zvonok.com: юзер сам звонит на проверочный номер) ─── */

// '74952960000' → '+7 495 296-00-00' — крупный читаемый формат
function formatCallmeNumber(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11) return raw;
  return `+${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}

function CallMeView({
  phone,
  callmeNumber,
  onVerified,
  onBack,
}: {
  phone: string;
  callmeNumber: string;
  onVerified: (isNewUser: boolean) => void;
  onBack: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(300);
  const [expired, setExpired] = useState(false);
  const verifiedRef = useRef(false);

  // Поллинг статуса каждые 3 сек: бэк вернёт accessToken, когда zvonok
  // подтвердит входящий звонок с номера юзера.
  useEffect(() => {
    if (expired) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await authApi.checkCallMe(phone);
        if (cancelled || verifiedRef.current) return;
        if (r.verified && r.accessToken) {
          verifiedRef.current = true;
          onVerified(!!r.isNewUser);
        } else if (!r.verified && !r.waiting && r.reason) {
          setExpired(true);
        }
      } catch {
        // сетевые сбои тихо ретраим следующим тиком
      }
    };
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phone, expired, onVerified]);

  // Обратный отсчёт (TTL ожидания на бэке — 5 минут)
  useEffect(() => {
    if (expired) return;
    const id = setInterval(() => {
      setSecondsLeft((p) => {
        if (p <= 1) { setExpired(true); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [expired]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const prettyNumber = formatCallmeNumber(callmeNumber);
  const telHref = `tel:+${callmeNumber.replace(/\D/g, '')}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col"
    >
      <AuthIllustration src={AUTH_IMAGES.code} alt="Позвоните для входа" />

      <button
        type="button"
        onClick={onBack}
        className="hidden md:flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors mb-5 self-start"
      >
        <ArrowLeft className="w-4 h-4" weight="bold" />
        Изменить номер
      </button>

      <h3 className="hidden md:block text-xl font-bold text-[var(--text-primary)] mb-2">
        Позвоните для входа
      </h3>

      {expired ? (
        <>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            Время ожидания звонка истекло. Запросите вход заново.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="w-full h-12 rounded-card bg-[var(--primary)] text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Попробовать снова
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--text-secondary)] mb-5 leading-relaxed">
            Позвоните с номера <span className="font-semibold text-[var(--text-primary)]">{phone}</span> на
            номер ниже — звонок <span className="font-semibold text-[var(--text-primary)]">бесплатный</span>,
            сбросится автоматически, и вы сразу войдёте.
          </p>

          {/* Проверочный номер — кликабелен на мобильных (открывает звонилку) */}
          <a
            href={telHref}
            className="block rounded-card border-2 border-[var(--primary)]/30 bg-[var(--primary-subtle)] px-4 py-5 text-center mb-4 hover:border-[var(--primary)]/60 transition-colors"
          >
            <span className="block text-2xl font-bold tracking-wide text-[var(--primary)] font-mono">
              {prettyNumber}
            </span>
            <span className="mt-1 flex items-center justify-center gap-1.5 text-xs text-[var(--text-secondary)]">
              <Phone className="w-3.5 h-3.5" weight="fill" />
              Нажмите, чтобы позвонить
            </span>
          </a>

          {/* Ожидание */}
          <div className="flex items-center justify-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
            <span className="inline-block w-2 h-2 rounded-pill bg-[var(--primary)] animate-pulse" />
            Ждём ваш звонок… {mm}:{ss}
          </div>
          <p className="text-xs text-center text-[var(--text-tertiary)]">
            После сброса звонка вход произойдёт автоматически в течение пары секунд
          </p>
        </>
      )}
    </motion.div>
  );
}

/* ─── main auth modal ─── */

export function AuthModal() {
  const router = useRouter();
  const {
    isAuthModalOpen,
    authModalView,
    verificationPhone,
    verificationRequestId,
    isLoading,
    openAuthModal,
    closeAuthModal,
    setAuthModalView,
    setVerificationData,
    clearVerificationData,
    setUser,
    setIsAuthenticated,
    setIsLoading,
  } = useAuthStore();

  // transition states: null | 'sending' | 'verifying' | 'success' | 'error'
  const [transition, setTransition] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  // Пауза до следующей попытки (бэк отдаёт retryAfter в секундах при 429)
  const [retryAfter, setRetryAfter] = useState(0);
  // Ошибка ввода кода: показывается ПОД полями, экран ввода при этом жив
  const [verifyError, setVerifyError] = useState('');
  const [verifyErrorNonce, setVerifyErrorNonce] = useState(0);
  // Call Me (zvonok): проверочный номер, на который звонит юзер
  const [callmeNumber, setCallmeNumber] = useState<string | null>(null);
  // Куда вернуть человека после входа (middleware кладёт путь в ?redirect=)
  const redirectAfterAuthRef = useRef<string | null>(null);

  // Обратный отсчёт паузы после 429 — пока идёт, «Попробовать снова» скрыта
  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setTimeout(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [retryAfter]);

  // middleware выкидывает с /profile на главную с ?auth=required — до сих пор
  // этот параметр никто не читал, и человек просто оказывался в каталоге.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') !== 'required') return;

    // Только внутренний путь: иначе ?redirect=https://evil.com стал бы открытым редиректом
    const rawRedirect = params.get('redirect');
    redirectAfterAuthRef.current = rawRedirect && /^\/(?!\/)/.test(rawRedirect) ? rawRedirect : null;
    openAuthModal();

    // Чистим хвост, иначе модалка всплывёт снова при возврате по истории
    params.delete('auth');
    params.delete('redirect');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [openAuthModal]);

  // api.ts на 401 стирает куку и шлёт 'auth:logout'. Слушателя не было: стор
  // оставался «залогинен», шапка вела в /profile, откуда middleware выкидывал
  // обратно на главную — замкнутый круг.
  useEffect(() => {
    const onLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, [setUser, setIsAuthenticated]);

  // Знаменатель воронки входа: ymAuthOpen/tmrAuthOpen были импортированы,
  // но не вызывались нигде — «сколько человек начали вход» посчитать было нечем.
  useEffect(() => {
    if (!isAuthModalOpen) return;
    ymAuthOpen();
    tmrAuthOpen();
  }, [isAuthModalOpen]);

  // Общий финал успешной авторизации (flash call и call me): cookie уже
  // установлен API-слоем — грузим профиль, метрики, редирект в профиль.
  const handleAuthSuccess = useCallback(async (isNewUser: boolean, method: AuthMethod = 'flash') => {
    try {
      const profile = await authApi.getProfile();
      setUser(profile);
      setIsAuthenticated(true);
      setTransition('success');
      ymAuthSuccess(isNewUser);
      tmrAuthSuccess();
      if (isNewUser) { ymRegistration(); tmrRegistration(); }
      trackAuth('auth_success', { method });
      toast.success(isNewUser ? 'Добро пожаловать! 🎉' : 'Вы успешно вошли');
      setTimeout(() => {
        closeAuthModal();
        setTransition(null);
        // Пришли с защищённой страницы — возвращаем туда, а не жёстко в профиль
        const target = redirectAfterAuthRef.current;
        redirectAfterAuthRef.current = null;
        router.push(target || '/profile');
      }, 1200);
    } catch {
      setTransition('error');
      setErrorMessage('Не удалось загрузить профиль. Попробуйте ещё раз.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Стабильная ссылка: CallMeView перезапускает поллинг при смене onVerified
  const handleCallMeVerified = useCallback((isNewUser: boolean) => {
    handleAuthSuccess(isNewUser, 'callme');
  }, [handleAuthSuccess]);

  const handleRequestCode = async (phone: string) => {
    setTransition('sending');
    setIsLoading(true);
    setRetryAfter(0);
    setVerifyError('');
    trackAuth('auth_started', { method: 'flash' });

    // 1) Новый способ — Call Me (zvonok): юзер сам звонит на проверочный номер.
    //    Бэк отвечает 501, пока zvonok не настроен → тихо падаем на flash call.
    try {
      const cm = await authApi.initiateCallMe(phone);
      if (cm.success && cm.callmeNumber) {
        setVerificationData(phone, 'call_me');
        setCallmeNumber(cm.callmeNumber);
        await new Promise((res) => setTimeout(res, 300));
        setTransition(null);
        setAuthModalView('callme');
        setIsLoading(false);
        return;
      }
    } catch {
      // not_configured / сбой zvonok → классический flash call ниже
    }

    // 2) Классический flash call: сервис звонит юзеру, вводим 4 цифры.
    try {
      const r = await authApi.initiateFlashCall(phone);
      if (r.success) {
        setVerificationData(phone, r.requestId);
        await new Promise((res) => setTimeout(res, 400));
        setTransition(null);
        setAuthModalView('verify');
        toast('Сейчас поступит звонок', {
          description: 'Введите последние 4 цифры входящего номера',
          icon: <Phone className="w-5 h-5 text-[var(--primary)]" weight="duotone" />,
        });
      }
    } catch (err: unknown) {
      // Текст пишет бэк: «не РФ-мобильный», «номер в ограниченном списке»,
      // «сервис звонков недоступен», лимит с паузой. Раньше всё это подменялось
      // одной фразой «Проверьте номер».
      const info = readApiError(err, 'Не удалось отправить код. Попробуйте позже.');
      setTransition('error');
      setErrorMessage(info.message);
      setRetryAfter(info.retryAfter ?? 0);
      trackAuth('auth_failed', {
        method: 'flash',
        http_status: info.status,
        error_code: info.errorCode,
        retry_after: info.retryAfter,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!verificationPhone || !verificationRequestId) return;
    // transition здесь намеренно НЕ трогаем: VerifyView сам показывает
    // «Проверяем код...» по isLoading, а подмена экрана стирала введённый код.
    setVerifyError('');
    setIsLoading(true);
    try {
      const r = await authApi.verifyFlashCall(verificationPhone, verificationRequestId, code);
      if (r.needRealCall && r.requestId) {
        // Код 6666 — бэкенд инициировал реальный звонок, обновляем requestId
        setVerificationData(verificationPhone, r.requestId);
        setTransition(null);
        // Экран ввода больше не пересоздаётся, поэтому поле чистим сами:
        // иначе в нём остались бы цифры от предыдущей попытки.
        setVerifyErrorNonce((n) => n + 1);
        toast.info(r.message || 'Сейчас поступит звонок, введите последние 4 цифры номера');
        setIsLoading(false);
        return;
      }
      if (r.success && r.accessToken) {
        await handleAuthSuccess(!!r.isNewUser, 'flash');
      } else {
        // 200 без токена: оставить экран молча нельзя — тот же код повторно
        // не отправится (submittedRef), и человек застрянет без объяснения.
        setVerifyError(r.message || 'Не удалось подтвердить код. Попробуйте ещё раз.');
        setVerifyErrorNonce((n) => n + 1);
      }
    } catch (err: unknown) {
      // Неверный код — не повод разрушать экран ввода: показываем ошибку под полями
      const info = readApiError(err, 'Неверный код подтверждения. Попробуйте ещё раз.');
      setVerifyError(info.message);
      setVerifyErrorNonce((n) => n + 1);
      trackAuth('auth_failed', {
        method: 'flash',
        http_status: info.status,
        error_code: info.errorCode,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVkAuth = async (code: string, deviceId: string) => {
    setTransition('verifying');
    setIsLoading(true);
    try {
      const redirectUri = `${window.location.origin}/auth/vk-callback`;
      const r = await authApi.vkAuth(code, deviceId, '', redirectUri);
      if (r.success && r.accessToken) {
        const profile = await authApi.getProfile();
        setUser(profile);
        setIsAuthenticated(true);
        setTransition('success');
        ymAuthSuccess(!!r.isNewUser);
        tmrAuthSuccess();
        if (r.isNewUser) { ymRegistration(); tmrRegistration(); }
        toast.success(r.isNewUser ? 'Добро пожаловать!' : 'Вы успешно вошли');
        if (r.phoneRequired) {
          toast.info('Добавьте номер телефона в профиле для полного доступа', { duration: 5000 });
        }
        setTimeout(() => {
          closeAuthModal();
          setTransition(null);
          router.push('/profile');
        }, 1200);
      }
    } catch (err: unknown) {
      setTransition('error');
      const errorData = (err as { response?: { data?: { phoneRequired?: boolean; error?: string } } })?.response?.data;
      if (errorData?.phoneRequired) {
        setErrorMessage('Для входа необходим номер телефона. Разрешите доступ к номеру в VK и попробуйте снова.');
      } else {
        setErrorMessage(errorData?.error || 'Не удалось авторизоваться через VK. Попробуйте другой способ.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setTransition(null);
    setErrorMessage('');
    setVerifyError('');
    setRetryAfter(0);
    clearVerificationData();
    setAuthModalView('login');
  };

  const handleRetry = () => {
    setTransition(null);
    setErrorMessage('');
    // go back to the view that was active before the error
    if (authModalView === 'verify' && verificationRequestId) {
      // Код уже запрошен — возвращаем к вводу. Новый звонок стоит денег и
      // внутри минуты всё равно упрётся в лимит 1 звонок/мин.
      setVerifyError('');
      return;
    }
    if (authModalView === 'callme' && verificationPhone) {
      // retry sending code
      handleRequestCode(verificationPhone);
    } else {
      setAuthModalView('login');
    }
  };

  const handleResend = () => {
    setVerifyError('');
    if (verificationPhone) handleRequestCode(verificationPhone);
  };

  // Determine mobile navbar title
  const getMobileTitle = () => {
    if (transition === 'error') return 'Ошибка';
    if (transition === 'success') return 'Готово';
    if (authModalView === 'verify') return 'Введите код';
    if (authModalView === 'callme') return 'Позвоните нам';
    return 'Вход в аккаунт';
  };

  const views = (
    <AnimatePresence mode="wait">
      {transition === 'sending' ? (
        <AuthSpinner key="sending" label="Отправляем код..." />
      ) : transition === 'verifying' ? (
        <AuthSpinner key="verifying" label="Проверяем..." />
      ) : transition === 'success' ? (
        <AuthSuccess key="success" label="Добро пожаловать!" />
      ) : transition === 'error' ? (
        <AuthError
          key="error"
          message={errorMessage}
          // Пока идёт пауза после 429 повтор бессмыслен — кнопку не показываем
          onRetry={retryAfter > 0 ? undefined : handleRetry}
          onBack={handleBack}
          retryAfter={retryAfter}
        />
      ) : authModalView === 'callme' && verificationPhone && callmeNumber ? (
        <CallMeView
          key="callme"
          phone={verificationPhone}
          callmeNumber={callmeNumber}
          onVerified={handleCallMeVerified}
          onBack={handleBack}
        />
      ) : authModalView === 'verify' && verificationPhone && verificationRequestId ? (
        <VerifyView
          key="verify"
          phone={verificationPhone}
          requestId={verificationRequestId}
          onVerify={handleVerifyCode}
          onBack={handleBack}
          onResend={handleResend}
          isLoading={isLoading}
          error={verifyError}
          errorNonce={verifyErrorNonce}
        />
      ) : (
        <PhoneView
          key="phone"
          onSubmitPhone={handleRequestCode}
          isLoading={isLoading}
        />
      )}
    </AnimatePresence>
  );

  return (
    <Dialog open={isAuthModalOpen} onOpenChange={(open) => !open && closeAuthModal()}>
      <DialogPrimitive.Portal>
        {/* Overlay: blurred on mobile, standard on desktop */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9500]',
            'md:bg-black/50 md:backdrop-blur-sm',
            'md:data-[state=open]:animate-[dialog-overlay-show_200ms_ease-out]',
            'md:data-[state=closed]:animate-[dialog-overlay-hide_150ms_ease-in]',
            'max-md:bg-black/40',
            'max-md:data-[state=open]:auth-backdrop-in',
            'max-md:data-[state=closed]:auth-backdrop-out',
          )}
        />

        {/* Content: fullscreen on mobile, centered card on desktop */}
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[9500] bg-[var(--surface-primary)] focus:outline-none',
            'auth-dialog-content',
            'md:left-1/2 md:top-1/2 md:w-full md:max-w-[420px] md:p-6',
            // Модалка — объём оставлен (ответ 35, второй вопрос = 2).
            'md:border md:border-[var(--border-default)] md:rounded-card md:shadow-deep',
            'md:data-[state=open]:animate-[dialog-content-show_250ms_cubic-bezier(0.16,1,0.3,1)]',
            'md:data-[state=closed]:animate-[dialog-content-hide_150ms_ease-in]',
            'max-md:inset-0 max-md:flex max-md:flex-col max-md:overflow-hidden',
            'max-md:data-[state=open]:auth-sheet-in',
            'max-md:data-[state=closed]:auth-sheet-out',
          )}
          onPointerDownOutside={(e) => {
            if (window.innerWidth < 768) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (window.innerWidth < 768) e.preventDefault();
          }}
        >
          {/* ── mobile navbar (hidden on desktop) ── */}
          <div className="px-4 pt-[max(env(safe-area-inset-top),14px)] pb-3 flex-shrink-0 md:hidden border-b border-[var(--border-default)] bg-[var(--surface-primary)]">
            <div className="relative flex items-center justify-between h-11">
              {/* back button — invisible on first step and terminal states */}
              <button
                type="button"
                onClick={handleBack}
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-pill',
                  'text-[var(--text-secondary)] active:text-[var(--text-primary)]',
                  'active:bg-[var(--surface-secondary)]',
                  'transition-colors duration-150',
                  (authModalView === 'login' || transition === 'success' || transition === 'error') && 'invisible',
                )}
              >
                <ArrowLeft className="h-5 w-5" weight="bold" />
              </button>

              {/* centered title */}
              <span className="text-base font-bold text-[var(--text-primary)] absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
                {getMobileTitle()}
              </span>

              {/* close button */}
              <DialogPrimitive.Close
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-pill',
                  'text-[var(--text-tertiary)] active:text-[var(--text-primary)]',
                  'active:bg-[var(--surface-secondary)]',
                  'transition-colors duration-150',
                )}
              >
                <X className="h-5 w-5" weight="bold" />
                <span className="sr-only">Закрыть</span>
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* ── desktop close button (hidden on mobile) ── */}
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 p-2 rounded-pill',
              'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]',
              'hover:bg-[var(--surface-secondary)]',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20',
              'hidden md:flex',
            )}
          >
            <X className="h-4 w-4" weight="bold" />
            <span className="sr-only">Закрыть</span>
          </DialogPrimitive.Close>

          {/* ── mobile: structured layout ── */}
          <div className="flex-1 overflow-y-auto overscroll-contain md:hidden">
            <div className="px-5 pt-8 pb-10 auth-content-enter">
              <div className="w-full max-w-[340px] mx-auto">
                {views}
              </div>
            </div>
          </div>

          {/* ── mobile: step dots at bottom (hidden on error/success) ── */}
          {!transition && (
            <div className="flex-shrink-0 py-4 md:hidden">
              <StepDots
                current={
                  authModalView === 'verify' || authModalView === 'callme' ? 1
                  : 0
                }
              />
            </div>
          )}

          {/* ── mobile: safe area bottom ── */}
          <div className="h-[env(safe-area-inset-bottom)] flex-shrink-0 md:hidden" />

          {/* ── desktop: direct content ── */}
          <div className="hidden md:block">
            {views}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export default AuthModal;
